import React, { useRef } from 'react';
import { useAtom } from 'jotai';
import { isMobileAtom } from '@atoms/appSettings';
import { iframeStyles as classes } from './styleObjects/IframeStyles';
import { getAnimationStyle } from './helpers/AnimationStyleHelper';
import { Heading, LazyLoadContainer } from '@web-for-marketing/react-ui';
import { isPreviewingAnimationAtom } from '@atoms/animation';
import { IframeData } from '@models/section/iFrameSection';
import { SectionAnimation } from '@models/section';
import { Grid } from '@mui/material';
import { useIframeResizer } from '@hooks/useIframeResizer';

export const defaultHeight = '200px';
export const defaultWidth = '80%';

interface IframeProps {
    lazyLoad: boolean;
    iframeComponent: IframeData;
    animations?: SectionAnimation;
}

export function Iframe({ iframeComponent, animations, lazyLoad }: IframeProps): React.ReactElement {
    const [isMobile] = useAtom(isMobileAtom);
    const [isPreviewingAnimation] = useAtom(isPreviewingAnimationAtom);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const height = isMobile ? iframeComponent.mobileHeight || defaultHeight : iframeComponent.height || defaultHeight;
    const width = isMobile ? iframeComponent.mobileWidth || defaultWidth : iframeComponent.width || defaultWidth;

    useIframeResizer({ elementRef: iframeRef, delay: 400 });

    return (
        <LazyLoadContainer
            component={Grid}
            lazyload={lazyLoad}
            css={classes.container}
            className={`${
                animations &&
                animations.sectionAnimation &&
                getAnimationStyle('sectionAnimation', true, animations, isPreviewingAnimation)
            }`}
        >
            {iframeComponent.url ? (
                <iframe
                    ref={iframeRef}
                    title={iframeComponent.title || 'Interactive graphic'}
                    css={[
                        classes.iframe,
                        !iframeComponent.iframeBorder ? classes.removeBorder : undefined,
                        { height: height, width: width },
                    ]}
                    data-src={lazyLoad ? iframeComponent.url : undefined}
                    src={lazyLoad ? undefined : iframeComponent.url}
                    data-testid='iframe'
                />
            ) : (
                <Heading variant='h2' css={[{ height: height, width: width }, classes.invalidHeading]}>
                    Url is not valid.
                </Heading>
            )}
        </LazyLoadContainer>
    );
}
